import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Flame, Loader2 } from 'lucide-react'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { getOccupancyReport } from '@/lib/statistics-api'
import {
    buildOccupancyGrid,
    isOftenFull,
    formatHourSlot,
    occupancyCellKey,
    occupancyStep,
    WEEKDAY_LABELS,
    WEEKDAYS,
    type OccupancyCell,
} from '@/lib/statistics'
import { cn } from '@/lib/utils'

export type OccupancyMetric = 'occupancy' | 'full'
type Metric = OccupancyMetric

const STEP_CLASSES = [
    'bg-muted text-muted-foreground',
    'bg-primary/15 text-foreground',
    'bg-primary/30 text-foreground',
    'bg-primary/50 text-foreground',
    'bg-primary/75 text-primary-foreground',
    'bg-primary text-primary-foreground',
]

const percentFormat = new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: 0 })
const decimalFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })

function metricValue(cell: OccupancyCell, metric: Metric) {
    return metric === 'occupancy' ? cell.occupancy : cell.fullShare
}

export function OccupancyHeatmap({ from, to, metric }: { from: string; to: string; metric: Metric }) {
    const [selectedKey, setSelectedKey] = useState<string | null>(null)

    const query = useQuery({
        queryKey: [queryKeys.admin, 'reports', 'occupancy', from, to],
        queryFn: () => getOccupancyReport({ from, to }),
    })

    const grid = useMemo(() => buildOccupancyGrid(query.data ?? []), [query.data])
    const selected = selectedKey ? grid.cells.get(selectedKey) : undefined

    if (query.isLoading) {
        return <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
    }

    if (query.isError) {
        return <p className="text-sm text-destructive">{getErrorMessage(query.error)}</p>
    }

    if (grid.hours.length === 0) {
        return (
            <p className="flex h-40 items-center justify-center text-muted-foreground">
                Keine vergangenen Termine in diesem Zeitraum.
            </p>
        )
    }

    return (
        <div className="space-y-4">
            <Legend />

            <div className="grid grid-cols-[3rem_repeat(7,minmax(0,1fr))] gap-1 text-xs">
                <div />
                {WEEKDAYS.map((weekday) => (
                    <div key={weekday} className="py-1 text-center font-medium text-muted-foreground">
                        {WEEKDAY_LABELS[weekday]}
                    </div>
                ))}
                {grid.hours.map((hour) => (
                    <HeatmapRow
                        key={hour}
                        hour={hour}
                        cells={grid.cells}
                        metric={metric}
                        selectedKey={selectedKey}
                        onSelect={setSelectedKey}
                    />
                ))}
            </div>

            <p className="text-xs text-muted-foreground">
                Termine sind nach Startzeit in Stundenblöcke zusammengefasst (z. B. 18:00 und 18:30 → 18–19). Nur vergangene, nicht abgesagte Termine im gewählten Zeitraum. Auslastung = bestätigte Buchungen ÷ Plätze, Gäste zählen nicht.
            </p>

            {selected ? <CellDetail cell={selected} /> : (
                <p className="text-sm text-muted-foreground">Slot anklicken für Details pro Kurs.</p>
            )}
        </div>
    )
}

function HeatmapRow({
    hour,
    cells,
    metric,
    selectedKey,
    onSelect,
}: {
    hour: number
    cells: Map<string, OccupancyCell>
    metric: Metric
    selectedKey: string | null
    onSelect: (key: string) => void
}) {
    return (
        <>
            <div className="flex items-center justify-end pr-1 tabular-nums text-muted-foreground">{formatHourSlot(hour)}</div>
            {WEEKDAYS.map((weekday) => {
                const key = occupancyCellKey(weekday, hour)
                const cell = cells.get(key)
                if (!cell) {
                    return <div key={key} className="h-9 rounded-md border border-dashed" />
                }
                const value = metricValue(cell, metric)
                const oftenFull = isOftenFull(cell)
                return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onSelect(key)}
                        title={`${WEEKDAY_LABELS[weekday]} ${formatHourSlot(hour)} Uhr: ${percentFormat.format(cell.occupancy)} Auslastung, ${cell.fullCount} von ${cell.sessionCount} Terminen ausgebucht`}
                        className={cn(
                            'flex h-9 items-center justify-center gap-0.5 rounded-md tabular-nums transition-shadow',
                            STEP_CLASSES[occupancyStep(value)],
                            selectedKey === key && 'ring-2 ring-foreground ring-offset-1 ring-offset-background',
                        )}
                    >
                        {percentFormat.format(value)}
                        {oftenFull && <Flame className="h-3 w-3 shrink-0" aria-label="oft ausgebucht" />}
                    </button>
                )
            })}
        </>
    )
}

function Legend() {
    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
                0 %
                {STEP_CLASSES.slice(1).map((className) => (
                    <span key={className} className={cn('h-3 w-4 rounded-sm', className)} />
                ))}
                100 %
            </span>
            <span className="flex items-center gap-1">
                <span className="h-3 w-4 rounded-sm border border-dashed" />
                kein Termin
            </span>
            <span className="flex items-center gap-1">
                <Flame className="h-3 w-3" aria-hidden="true" />
                oft ausgebucht (≥ 75 % der Termine voll)
            </span>
        </div>
    )
}

function CellDetail({ cell }: { cell: OccupancyCell }) {
    return (
        <div className="space-y-3 rounded-md border p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-medium">{WEEKDAY_LABELS[cell.weekday]} · {formatHourSlot(cell.hour)} Uhr</h3>
                <p className="text-sm text-muted-foreground">
                    {percentFormat.format(cell.occupancy)} Auslastung · ausgebucht in {cell.fullCount} von {cell.sessionCount} Terminen
                </p>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Beginn</TableHead>
                        <TableHead>Kurs</TableHead>
                        <TableHead className="text-right">Termine</TableHead>
                        <TableHead className="text-right">Ø belegt</TableHead>
                        <TableHead className="text-right">Auslastung</TableHead>
                        <TableHead className="text-right">Ausgebucht</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {cell.courses.map((course) => (
                        <TableRow key={`${course.courseId}-${course.startTime}`}>
                            <TableCell className="tabular-nums">{course.startTime}</TableCell>
                            <TableCell>{course.courseName}</TableCell>
                            <TableCell className="text-right tabular-nums">{course.sessionCount}</TableCell>
                            <TableCell className="text-right tabular-nums">
                                {decimalFormat.format(course.bookedSum / course.sessionCount)} / {decimalFormat.format(course.capacitySum / course.sessionCount)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{percentFormat.format(course.occupancy)}</TableCell>
                            <TableCell className="text-right tabular-nums">{course.fullCount} von {course.sessionCount}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    )
}
